import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { bookPlace, checkBooking, unbookPlace } from 'src/api/bookings';
import { SharedPeriod, sharePlace } from 'src/api/places';
import {
  BackgroundData,
  getPlanBackground,
  ParkingPlaceWithStatus,
  readParkingPlaceMicroCards,
} from 'src/api/plan';
import { BookingObject } from 'src/types';
import { defaultServerError } from 'src/utils/consts';
import { getDateTime } from 'src/utils/getDateTime';
import { toBookingObject } from 'src/utils/toBookingObject';

import { messagesActions } from '../messages/slice';
import { sidesheetActions } from '../sidesheet/slice';
import { StoreState, ThunkConfig } from '../types';

import {
  getCurrentParkingPlace,
  getParkingLocation,
  getParkingState,
  getParkingTimePeriodUTC,
  getParkingTown,
} from './selectors';
import { parkingActions } from './slice';

export interface ParkingPlacesAllData {
  backgroundData: BackgroundData | undefined;
  bookingObjects: Map<string, BookingObject>;
}

export const fetchParkingPlaces = createAsyncThunk<
  ParkingPlacesAllData,
  boolean,
  ThunkConfig<string>
>(
  'parking/fetchParkingPlaces',
  async (withBackground, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as StoreState;
      const { floor } = getParkingLocation(state);
      const { from, to } = getParkingTimePeriodUTC(state);

      const data: ParkingPlacesAllData = {
        backgroundData: undefined,
        bookingObjects: new Map(),
      };

      const floorId = floor?.floorId;

      if (!floorId || !from || !to) return data;

      const promises: [
        Promise<ParkingPlaceWithStatus[]>,
        Promise<ParkingPlacesAllData['backgroundData']>,
      ] = [
        readParkingPlaceMicroCards({ floorId, searchFrom: from, searchTo: to }),
        withBackground
          ? getPlanBackground(floorId)
          : Promise.resolve(undefined),
      ];

      const [parkingPlaces, backgroundData] = await Promise.all(promises);

      data.backgroundData = backgroundData;
      for (const parkingPlace of parkingPlaces) {
        const bookingObject = toBookingObject(parkingPlace);
        data.bookingObjects.set(bookingObject.mapPlaceId, bookingObject);
      }

      return data;
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as StoreState;

      const { floor } = getParkingLocation(state);
      const { from, to } = getParkingTimePeriodUTC(state);

      if (!floor?.floorId || !from || !to) return false;
    },
  },
);

export interface BookParkingParams {
  responsible?: string;
  guestName?: string;
  carModel: string;
  carNumber: string;
  needPass: boolean;
}

export const bookParking = createAsyncThunk<
  boolean,
  BookParkingParams,
  ThunkConfig<string>
>(
  'parking/book',
  async (
    { responsible, guestName, carModel, carNumber, needPass },
    { getState, dispatch },
  ) => {
    try {
      await dispatch(parkingActions.validateDate());
      await dispatch(parkingActions.validateTime());

      const state = getState() as StoreState;

      const parking = getCurrentParkingPlace(state);
      const { dateError, timeError } = getParkingState(state);
      if (dateError || timeError) return false;

      const { from, to } = getParkingTimePeriodUTC(state);

      if (
        !parking?.id ||
        !from ||
        !to ||
        !(responsible || guestName) ||
        !carModel ||
        !carNumber
      )
        return false;

      const bookPlaceResponse = await bookPlace({
        placeId: parking.id,
        dateFrom: from,
        dateTo: to,
        responsible,
        bookData: {
          guestName,
          carModel,
          carNumber,
          needPass,
        },
      });

      if (!bookPlaceResponse.accepted)
        throw new Error(bookPlaceResponse.rejectedReason ?? defaultServerError);

      const checkBookPlaceResponse = await checkBooking({
        commandId: bookPlaceResponse.commandId,
        maxDurationExceededError:
          'Заявка на бронь отправлена, подтверждение придёт на email',
      });
      if (checkBookPlaceResponse.processed) {
        await dispatch(sidesheetActions.close());
        await dispatch(fetchParkingPlaces(false));
        await dispatch(
          messagesActions.show({
            message: `Парковочное место ${parking.title} успешно забронировано`,
          }),
        );
      }
      return true;
    } catch (e) {
      const message = (e as AxiosError).message;

      dispatch(messagesActions.show({ message }));
      return false;
    }
  },
);

export const unbookParking = createAsyncThunk<
  boolean,
  void,
  ThunkConfig<string>
>('parking/unbook', async (_, { getState, dispatch }) => {
  try {
    const state = getState() as StoreState;

    const parking = getCurrentParkingPlace(state);

    if (!parking?.status?.bookingId) return false;

    const unbookPlaceResponse = await unbookPlace(parking.status.bookingId);

    if (!unbookPlaceResponse.accepted)
      throw new Error(unbookPlaceResponse.rejectedReason ?? defaultServerError);

    const checkBookPlaceResponse = await checkBooking({
      commandId: unbookPlaceResponse.commandId,
      maxDurationExceededError:
        'Заявка на отмену брони отправлена, подтверждение придёт на email',
    });

    if (checkBookPlaceResponse.processed) {
      await dispatch(sidesheetActions.close());
      await dispatch(fetchParkingPlaces(false));
      await dispatch(
        messagesActions.show({
          message: `Бронь парковочного места ${parking.title} отменена`,
        }),
      );
    }
    return true;
  } catch (e) {
    const message = (e as AxiosError).message;

    dispatch(messagesActions.show({ message }));
    return false;
  }
});

export interface BookParkingParams {
  responsible?: string;
  guestName?: string;
  carModel: string;
  carNumber: string;
  needPass: boolean;
}

export const shareParkingPlace = createAsyncThunk<
  boolean,
  string[],
  ThunkConfig<string>
>('parking/share', async (dates, { getState, dispatch }) => {
  try {
    const state = getState() as StoreState;

    const parking = getCurrentParkingPlace(state);
    const town = getParkingTown(state);

    if (!parking?.id) return false;

    const sharedPeriods: SharedPeriod[] = dates.map(d => {
      const from = getDateTime({
        date: new Date(d),
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
        timezoneOffset: town?.timeZone,
        toUTC: true,
      });
      const to = new Date(from);
      to.setDate(to.getDate() + 1);

      return { from: from.toISOString(), to: to.toISOString() };
    });

    const sharePlaceResponse = await sharePlace({
      placeId: parking.id,
      sharedPeriods,
    });

    if (!sharePlaceResponse.success)
      throw new Error(sharePlaceResponse.errorMessage ?? defaultServerError);

    await dispatch(
      messagesActions.show({
        message: `Даты шеринга парковочного места ${parking.title} успешно обновлены`,
      }),
    );
    await dispatch(fetchParkingPlaces(false));

    return true;
  } catch (e) {
    const message = (e as AxiosError).message;

    dispatch(messagesActions.show({ message }));
    return false;
  }
});
